import * as React from "react";
import {
  Box,
  Grid,
  InputLabel,
  TextField,
  Typography,
  IconButton,
  Button,
  InputAdornment,
  Link,
  MenuItem,
  Select,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import Modifiers from "./Modifiers";
import DiagnosisCodes from "./DiagnosisCodes";
import DatesOfService from "./DatesOfService";
import RenderingProvider from "./RenderingProvider";
import PlaceOfService from "./PlaceOfService";
import FamilyPlanning from "./FamilyPlanning";
import EmergencyService from "./EmergencyService";
import { AuthenticationContext, ImagesContext } from "../Context";
import { ChargeLine } from "./types";
import { getHeaders } from "../utils/HeaderHelper";
import { formatMonetaryValue, formatMonetaryInput } from "../utils/MonetaryFormatHelper";

interface ChargeFormProps {
  index: number;
  charge: ChargeLine;
  onChange: (
    index: number,
    field: keyof ChargeLine,
    value: ChargeLine[keyof ChargeLine]
  ) => void;
  onRemove: (index: number) => void;
  encounterBilling: any;
  totalModifiers: number;
  totalDiagnosisCodes: number;
  renderingProviders: any[];
  placeOfServiceList: any;
  claimStatus: string;
  showNDCSection: any;
  setShowNDCSection: any;
}

const ChargeForm: React.FC<ChargeFormProps> = ({
  index,
  charge,
  onChange,
  onRemove,
  encounterBilling,
  totalModifiers,
  totalDiagnosisCodes,
  renderingProviders,
  placeOfServiceList,
  claimStatus,
  showNDCSection,
  setShowNDCSection,
}) => {
  const [activeSection, setActiveSection] = React.useState(1);
  const authenticationSetting = React.useContext(AuthenticationContext);
  const images = React.useContext(ImagesContext);
  // Track if the user manually edited charges so we don't auto-overwrite
  const userEditedChargeRef = React.useRef<boolean>(false);
  const userEditedNdcChargeRef = React.useRef<boolean>(false);
  // Track focus state for formatting display values
  const [chargeAmtFocused, setChargeAmtFocused] = React.useState(false);
  const [ndcChargeFocused, setNdcChargeFocused] = React.useState(false);
  // Auto-populate pricing when component mounts with pre-filled codes
  React.useEffect(() => {
    const autoPopulatePricing = async () => {
      // Check CPT code pricing
      if (
        charge.cpt_code &&
        charge.cpt_code.length >= 3 &&
        !userEditedChargeRef.current &&
        !charge.charge_amt
      ) {
        const price = await lookupCptPrice(charge.cpt_code);
        if (price !== null && !charge.charge_amt) {
          onChange(index, "charge_amt", formatMonetaryValue(price.toString()));
        }
      }

      // Check NDC code pricing
      const cleanedNdc = (charge.ndc_code || "").replace(/\D/g, "");
      if (
        cleanedNdc &&
        !userEditedNdcChargeRef.current &&
        !charge.ndc_charge
      ) {
        const price = await lookupNdcPrice(cleanedNdc);
        if (price !== null && !charge.ndc_charge) {
          onChange(index, "ndc_charge", formatMonetaryValue(price.toString()));
          if (cleanedNdc !== charge.ndc_code) {
            onChange(index, "ndc_code", cleanedNdc);
          }
        }
      }
    };

    autoPopulatePricing();
  }, [
    charge.cpt_code,
    charge.ndc_code,
    charge.charge_amt,
    charge.ndc_charge,
    index,
    onChange,
  ]);

  const handleArrayChange = (
    field: "modifiers" | "diagnosis_codes",
    i: number,
    value: string
  ) => {
    const updated = [...(charge[field] || [])];
    updated[i] = { code: value, index: i, id: charge[field][i].id || null };
    onChange(index, field, updated as any);
  };

  const addArrayField = (field: "modifiers" | "diagnosis_codes") => {
    const currentArray = charge[field] || [];
    const newItem = {
      code: "",
      index: currentArray.length,
      id: null,
    };
    const updated = [...currentArray, newItem];
    onChange(index, field, updated as any);
  };

  const removeArrayField = (
    field: "modifiers" | "diagnosis_codes",
    i: number
  ) => {
    const currentArray = [...(charge[field] || [])];
    currentArray.splice(i, 1);

    const updated = currentArray.map((item, idx) => ({
      ...item,
      index: idx,
    }));

    onChange(index, field, updated as any);
  };

  const getChargeTitle = (i: number) =>
    `Charge Line ${["One", "Two", "Three", "Four", "Five", "Six"][i]}`;

  const additionalDetailsButtons = [
    { id: 1, label: "Dates of Service" },
    { id: 2, label: "Rendering Provider" },
    { id: 3, label: "Place of Service" },
    { id: 4, label: "Family Planning" },
    { id: 5, label: "Emergency" },
  ];

  const displayNDCSection = () => setShowNDCSection(true);
  const handleRemoveNDCSection = () => {
    setShowNDCSection(false);
    onChange(index, "ndc_code", "");
    onChange(index, "ndc_charge", "");
    onChange(index, "unit_modifier", "");
  };

  const unitModifierOptions = [
    { value: "F2", label: "F2" },
    { value: "GR", label: "GR" },
    { value: "ME", label: "ME" },
    { value: "ML", label: "ML" },
    { value: "UN", label: "UN" },
  ];

  const lookupCptPrice = async (cptCode: string) => {
    if (!cptCode || cptCode.length < 3) return null;

    try {
      const response = await fetch(
        `/pricing/lookup_cpt_price?cpt_code=${cptCode}`,
        {
          method: "GET",
          headers: getHeaders(authenticationSetting.csrfToken),
        }
      );
      const result = await response.json();
      return result.success ? result.resource.price : null;
    } catch (error) {
      console.error("Error looking up CPT price:", error);
      return null;
    }
  };

  const lookupNdcPrice = async (ndcCode: string) => {
    if (!ndcCode) return null;

    try {
      const response = await fetch(
        `/pricing/lookup_ndc_price?ndc_code=${ndcCode}`,
        {
          method: "GET",
          headers: getHeaders(authenticationSetting.csrfToken),
        }
      );
      const result = await response.json();
      return result.success ? result.resource.price : null;
    } catch (error) {
      console.error("Error looking up NDC price:", error);
      return null;
    }
  };

  const handleCptCodeChange = async (value: string) => {
    onChange(index, "cpt_code", value);
    userEditedChargeRef.current = false;

    if (value && value.length >= 3) {
      const price = await lookupCptPrice(value);
      if (price !== null && !userEditedChargeRef.current) {
        onChange(index, "charge_amt", formatMonetaryValue(price.toString()));
      }
    }
  };

  const handleNdcCodeChange = async (value: string) => {
    const ndcCode = value.replace(/\D/g, "");
    onChange(index, "ndc_code", ndcCode);
    userEditedNdcChargeRef.current = false;

    if (ndcCode) {
      const price = await lookupNdcPrice(ndcCode);
      if (price !== null && !userEditedNdcChargeRef.current) {
        onChange(index, "ndc_charge", formatMonetaryValue(price.toString()));
      }
    }
  };

  const renderActiveSection = () => {
    switch (activeSection) {
      case 1:
        return (
          <DatesOfService
            from_date={charge?.from_date}
            to_date={charge?.to_date}
            onChange={(field, value) => onChange(index, field, value)}
            claimStatus={claimStatus}
          />
        );
      case 2:
        return (
          <RenderingProvider
            renderingProviderId={charge?.rendering_provider_id}
            renderingProviders={renderingProviders}
            onChange={(field: keyof ChargeLine, value) =>
              onChange(index, field, value)
            }
            claimStatus={claimStatus}
          />
        );
      case 3:
        return (
          <PlaceOfService
            place_of_service={
              charge?.place_of_service || encounterBilling?.place_of_service
            }
            options={placeOfServiceList}
            onChange={(field, value) => onChange(index, field, value)}
            claimStatus={claimStatus}
          />
        );
      case 4:
        return (
          <FamilyPlanning
            epsdt={charge.epsdt}
            family_planning={charge.family_planning}
            onChange={(field, value) => onChange(index, field, value)}
            claimStatus={claimStatus}
          />
        );
      case 5:
        return (
          <EmergencyService
            emergencyServices={charge.emergency_services}
            onChange={(field, value) => onChange(index, field, value)}
            claimStatus={claimStatus}
          />
        );
      default:
        return <Box />;
    }
  };

  return (
    <Grid container sx={{ paddingBottom: "24px" }}>
      <Grid item xs={12}>
        <Box
          sx={{
            backgroundColor: "white",
            borderRadius: "4px",
            boxShadow: "0px 4px 4px 0px #00000040",
          }}
        >
          <Box
            sx={{
              padding: "24px",
              display: "flex",
              boxShadow: "0px 7px 7px 0px #00000040",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                rowGap: "10px",
              }}
            >
              <Typography
                sx={{
                  fontSize: "18px",
                  lineHeight: "18px",
                  fontWeight: 700,
                  letterSpacing: "1px",
                  color: "#4A4442",
                }}
              >
                {getChargeTitle(index)}
              </Typography>
              <Typography
                sx={{
                  fontSize: "14px",
                  lineHeight: "18px",
                  fontWeight: 500,
                  letterSpacing: "1px",
                  color: "#707070",
                }}
              >
                Rendering Provider:{" "}
                {authenticationSetting?.currentUser?.name ?? "N/A"} • From:{" "}
                {charge?.from_date ?? "N/A"} • To: {charge?.to_date ?? "N/A"}
              </Typography>
            </Box>
            <Box>
              {index > 0 && (
                <IconButton
                  onClick={() => onRemove(index)}
                  sx={{ padding: "0px" }}
                >
                  <CloseIcon />
                </IconButton>
              )}
            </Box>
          </Box>
          <Box
            sx={{
              padding: "24px 24px",
            }}
          >
            <Grid container wrap="nowrap" alignItems="center" spacing={0}>
              <Grid item xs={2}>
                <Grid container columnSpacing={3} rowSpacing={1}>
                  <Grid item xs={6}>
                    <InputLabel
                      htmlFor={`cpt_code_${index}`}
                      sx={{
                        color: "#A29D9B",
                        fontWeight: 700,
                        fontSize: "12px",
                        lineHeight: "26px",
                      }}
                    >
                      CPT Code*
                    </InputLabel>
                    <Box className="field-container" sx={{ marginLeft: "0px" }}>
                      <TextField
                        id={`cpt_code_${index}`}
                        size="small"
                        value={charge.cpt_code}
                        variant="outlined"
                        className="the-field"
                        placeholder="XXXXX"
                        required
                        autoComplete="off"
                        disabled={
                          claimStatus === "submitted" ||
                          claimStatus === "pending_submission"
                        }
                        onChange={(e) => handleCptCodeChange(e.target.value)}
                        sx={{
                          width: "100% !important",
                        }}
                        slotProps={{
                          htmlInput: {
                            maxLength: 5,
                          },
                        }}
                      />
                    </Box>
                  </Grid>
                  <Grid item xs={6}>
                    <InputLabel
                      htmlFor={`charge_amt_${index}`}
                      sx={{
                        color: "#A29D9B",
                        fontWeight: 700,
                        fontSize: "12px",
                        lineHeight: "26px",
                      }}
                    >
                      Charge*
                    </InputLabel>
                    <Box className="field-container" sx={{ marginLeft: "0px" }}>
                      <TextField
                        id={`charge_${index}`}
                        size="small"
                        value={(() => {
                          if (chargeAmtFocused) {
                            return charge.charge_amt || '';
                          }
                          return charge.charge_amt ? formatMonetaryValue(charge.charge_amt) : '';
                        })()}
                        variant="outlined"
                        className="the-field"
                        placeholder="####.##"
                        required
                        autoComplete="off"
                        disabled={
                          claimStatus === "submitted" ||
                          claimStatus === "pending_submission"
                        }
                        onFocus={() => setChargeAmtFocused(true)}
                        onChange={(e) => {
                          const charge = formatMonetaryInput(e.target.value);
                          if (charge !== null) {
                            onChange(index, "charge_amt", charge);
                            userEditedChargeRef.current = true;
                          }
                        }}
                        onBlur={(e) => {
                          setChargeAmtFocused(false);
                          const formatted = formatMonetaryValue(e.target.value);
                          if (formatted !== '') {
                            onChange(index, "charge_amt", formatted);
                          }
                        }}
                        sx={{
                          width: "100% !important",
                          "& .MuiInputBase-input": {
                            paddingLeft: charge.charge_amt && "0px !important",
                          },
                          "& .MuiOutlinedInput-root": {
                            paddingLeft: 0,
                          },
                        }}
                        slotProps={{
                          input: {
                            inputMode: "numeric",
                            startAdornment: charge.charge_amt ? (
                              <InputAdornment
                                position="start"
                                sx={{
                                  marginRight: "0px",
                                  paddingLeft: "10.5px",
                                }}
                              >
                                <Typography
                                  sx={{
                                    color:
                                      claimStatus === "submitted" ||
                                      claimStatus === "pending_submission"
                                        ? "rgba(0, 0, 0, 0.38)"
                                        : "#4A4442",
                                    fontSize: "14px",
                                  }}
                                >
                                  $
                                </Typography>
                              </InputAdornment>
                            ) : undefined,
                          },
                        }}
                      />
                    </Box>
                  </Grid>
                  <Grid item xs={6}>
                    <InputLabel
                      htmlFor={`diagnosis_codes_${index}`}
                      sx={{
                        color: "#A29D9B",
                        fontWeight: 700,
                        fontSize: "12px",
                        lineHeight: "26px",
                      }}
                    >
                      Diagnosis Codes*
                    </InputLabel>
                    <Box className="field-container" sx={{ marginLeft: "0px" }}>
                      <TextField
                        id={`diagnosis_codes_${index}`}
                        size="small"
                        value={charge.diagnosis_codes[0].code}
                        variant="outlined"
                        className="the-field"
                        placeholder="XXXXXXX"
                        required
                        autoComplete="off"
                        disabled={
                          claimStatus === "submitted" ||
                          claimStatus === "pending_submission"
                        }
                        onChange={(e) =>
                          handleArrayChange(
                            "diagnosis_codes",
                            0,
                            e.target.value
                          )
                        }
                        sx={{
                          width: "100% !important",
                        }}
                        slotProps={{ htmlInput: { maxLength: 7 } }}
                      />
                    </Box>
                  </Grid>
                  <Grid item xs={6}>
                    <InputLabel
                      htmlFor={`units_${index}`}
                      sx={{
                        color: "#A29D9B",
                        fontWeight: 700,
                        fontSize: "12px",
                        lineHeight: "26px",
                      }}
                    >
                      Units*
                    </InputLabel>
                    <Box className="field-container" sx={{ marginLeft: "0px" }}>
                      <TextField
                        id={`units_${index}`}
                        size="small"
                        value={charge.units}
                        variant="outlined"
                        className="the-field"
                        placeholder="XXXXXXX"
                        required
                        autoComplete="off"
                        disabled={
                          claimStatus === "submitted" ||
                          claimStatus === "pending_submission"
                        }
                        onChange={(e) => {
                          const units = e.target.value.replace(/\D/g, "");
                          onChange(index, "units", units);
                        }}
                        sx={{
                          width: "100% !important",
                        }}
                        slotProps={{ htmlInput: { inputMode: "numeric" } }}
                      />
                    </Box>
                  </Grid>
                </Grid>
              </Grid>

              <Grid
                item
                sx={{ flex: "0 0 1px", mx: "16px", alignSelf: "stretch" }}
              >
                <Box
                  sx={{ width: "1px", height: "100%", bgcolor: "#D9D9D9" }}
                />
              </Grid>

              <Grid item xs={4}>
                <Grid container columnSpacing={2}>
                  <Grid item xs={6}>
                    <Modifiers
                      modifiers={charge.modifiers}
                      onChange={(i, val) =>
                        handleArrayChange("modifiers", i, val)
                      }
                      onAdd={() => addArrayField("modifiers")}
                      onRemove={(i) => removeArrayField("modifiers", i)}
                      totalModifiers={totalModifiers}
                      claimStatus={claimStatus}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <DiagnosisCodes
                      diagnosisCodes={charge.diagnosis_codes.slice(1)}
                      onChange={(i, val) =>
                        handleArrayChange("diagnosis_codes", i, val)
                      }
                      onAdd={() => addArrayField("diagnosis_codes")}
                      onRemove={(i) => removeArrayField("diagnosis_codes", i)}
                      totalDiagnosisCodes={totalDiagnosisCodes}
                      claimStatus={claimStatus}
                    />
                  </Grid>
                </Grid>
              </Grid>

              <Grid
                item
                sx={{ flex: "0 0 1px", mx: "16px", alignSelf: "stretch" }}
              >
                <Box
                  sx={{ width: "1px", height: "100%", bgcolor: "#D9D9D9" }}
                />
              </Grid>

              <Grid item xs={1.3}>
                <Typography
                  sx={{
                    color: "#A29D9B",
                    fontWeight: 700,
                    fontSize: "12px",
                    lineHeight: "26px",
                  }}
                >
                  Additional Details*
                </Typography>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    rowGap: "7px",
                  }}
                >
                  {additionalDetailsButtons.map((button) => (
                    <Button
                      key={button.id}
                      variant={
                        activeSection === button.id ? "contained" : "outlined"
                      }
                      onClick={() => setActiveSection(button.id)}
                      fullWidth
                      sx={{
                        color: activeSection === button.id ? "white" : "black",
                        backgroundColor:
                          activeSection !== button.id && "#EFE9E7",
                        fontSize: "8px",
                        fontWeight: 500,
                        lineHeight: "20px",
                        paddingTop: "0px",
                        paddingBottom: "0px",
                        border: "none",
                      }}
                    >
                      {button.label}
                    </Button>
                  ))}
                </Box>
              </Grid>

              <Grid
                item
                xs={4.7}
                sx={{
                  marginLeft: "16px",
                  boxShadow: "0px 4px 4px 0px #00000040",
                  padding: "16px",
                }}
              >
                {renderActiveSection()}
              </Grid>
            </Grid>

            <Box>
              {!showNDCSection && !charge?.ndc_code && (
                <Link className="row" style={{}} onClick={displayNDCSection}>
                  <img
                    src={images.add_icon}
                    alt="Add Charge"
                    style={{ width: 18, maxHeight: 18 }}
                  />
                  <span
                    className="app-user-text disable-pointer"
                    style={{
                      marginLeft: "8px",
                      fontSize: "12px",
                      color: "#A29D9B",
                    }}
                  >
                    Add NDC
                  </span>
                </Link>
              )}
            </Box>

            {(showNDCSection || charge?.ndc_code) && (
              <Box>
                <div
                  className="divider"
                  style={{ margin: "24px 0px 10px 0px" }}
                ></div>
                <Grid container wrap="nowrap" alignItems="center" spacing={3}>
                  <Grid item xs={2}>
                    <InputLabel
                      htmlFor={`ndc_code_${index}`}
                      sx={{
                        color: "#A29D9B",
                        fontWeight: 700,
                        fontSize: "12px",
                        lineHeight: "26px",
                      }}
                    >
                      NDC Code*
                    </InputLabel>
                    <Box className="field-container" sx={{ marginLeft: "0px" }}>
                      <TextField
                        id={`ncd_code_${index}`}
                        size="small"
                        value={(charge.ndc_code || "").replace(/\D/g, "")}
                        variant="outlined"
                        className="the-field"
                        placeholder="XXXXXXXXXXX"
                        autoComplete="off"
                        disabled={
                          claimStatus === "submitted" ||
                          claimStatus === "pending_submission"
                        }
                        onChange={(e) => {
                          handleNdcCodeChange(e.target.value);
                        }}
                        sx={{
                          width: "100% !important",
                        }}
                        slotProps={{
                          htmlInput: { inputMode: "numeric", maxLength: 11 },
                          input: {
                            inputMode: "numeric",
                            endAdornment: (
                              <InputAdornment position="end">
                                <CloseIcon
                                  fontSize="small"
                                  sx={{
                                    cursor: "pointer",
                                  }}
                                  onClick={handleRemoveNDCSection}
                                />
                              </InputAdornment>
                            ),
                          },
                        }}
                      />
                    </Box>
                  </Grid>
                  <Grid item xs={2}>
                    <InputLabel
                      htmlFor={`ndc_charge_${index}`}
                      sx={{
                        color: "#A29D9B",
                        fontWeight: 700,
                        fontSize: "12px",
                        lineHeight: "26px",
                      }}
                    >
                      NDC Charge*
                    </InputLabel>
                    <Box className="field-container" sx={{ marginLeft: "0px" }}>
                      <TextField
                        id={`ndc_charge_${index}`}
                        size="small"
                        value={(() => {
                          if (ndcChargeFocused) {
                            return charge.ndc_charge || '';
                          }
                          if (charge.ndc_charge) {
                            return formatMonetaryValue(charge.ndc_charge);
                          }
                          return '';
                        })()}
                        variant="outlined"
                        className="the-field"
                        placeholder="####.##"
                        autoComplete="off"
                        required
                        disabled={
                          claimStatus === "submitted" ||
                          claimStatus === "pending_submission"
                        }
                        onFocus={() => setNdcChargeFocused(true)}
                        onChange={(e) => {
                          const ndc_charge = formatMonetaryInput(e.target.value);
                          if (ndc_charge !== null) {
                            onChange(index, "ndc_charge", ndc_charge);
                            userEditedNdcChargeRef.current = true;
                          }
                        }}
                        onBlur={(e) => {
                          setNdcChargeFocused(false);
                          const formatted = formatMonetaryValue(e.target.value);
                          if (formatted !== '') {
                            onChange(index, "ndc_charge", formatted);
                          }
                        }}
                        sx={{
                          width: "100% !important",
                          "& .MuiInputBase-input": {
                            paddingLeft: charge.ndc_charge && "0px !important",
                          },
                          "& .MuiOutlinedInput-root": {
                            paddingLeft: 0,
                          },
                        }}
                        slotProps={{
                          input: {
                            inputMode: "numeric",
                            startAdornment: charge.ndc_charge ? (
                              <InputAdornment
                                position="start"
                                sx={{
                                  marginRight: "0px",
                                  paddingLeft: "10.5px",
                                }}
                              >
                                <Typography
                                  sx={{
                                    color:
                                      claimStatus === "submitted" ||
                                      claimStatus === "pending_submission"
                                        ? "rgba(0, 0, 0, 0.38)"
                                        : "#4A4442",
                                    fontSize: "14px",
                                  }}
                                >
                                  $
                                </Typography>
                              </InputAdornment>
                            ) : undefined,
                          },
                        }}
                      />
                    </Box>
                  </Grid>
                  <Grid item xs={2}>
                    <InputLabel
                      htmlFor={`unit_modifier_${index}`}
                      sx={{
                        color: "#A29D9B",
                        fontWeight: 700,
                        fontSize: "12px",
                        lineHeight: "26px",
                      }}
                    >
                      Unit Modifier*
                    </InputLabel>
                    <Box className="field-container" sx={{ marginLeft: "0px" }}>
                      <Select
                        id={`unit_modifier_${index}`}
                        value={charge?.unit_modifier}
                        required
                        variant="outlined"
                        className="the-field"
                        disabled={
                          claimStatus === "submitted" ||
                          claimStatus === "pending_submission"
                        }
                        displayEmpty
                        onChange={(event) => {
                          onChange(index, "unit_modifier", event.target.value);
                        }}
                        renderValue={(selected) => {
                          if (!selected) {
                            return <>Select Modifier</>;
                          }
                          return (
                            unitModifierOptions.find(
                              (opt) => opt.value === selected
                            )?.label || selected
                          );
                        }}
                      >
                        {unitModifierOptions.map((option) => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </Box>
                  </Grid>
                </Grid>
              </Box>
            )}
          </Box>
        </Box>
      </Grid>
    </Grid>
  );
};

export default ChargeForm;
